import type { ReactNode } from 'react'
import { Tooltip, CardSpinner } from 'admination-react'
import { anzeigeName, hatRueckzug, type DashboardCoaching } from '@/core/types'
import type { CoachingModal as Modal } from '@/core/state/appStore'
import type { BoardColumnConfig, BoardViewConfig, CardMark, CardTone } from '@/core/views/viewConfig'
import { ampelFor, tageInSpalte, type AmpelSchwellen } from '@/core/lib/ampel'
import { aufgabeForName } from '@/core/data/aufgabe'
import { phaseLandingTabFor } from '@/core/hooks/usePhaseNav'
import { cardIncompleteCovers, descriptionFor, type CardIncomplete } from '@/variants'
import { currentVariant } from '@/core/data/session'
import { withCompoundBreaks } from '@/core/lib/titleSyllables'
import { coachingViewConfig, zurueckgezogenAm } from './coachingViewConfig'

const ZURUECKGEZOGEN = 'zurueckgezogen'
const ZURUECKGEZOGEN_KARTE = 'open-zurueckgezogen'

export const coachingBoardGroup = (coaching: DashboardCoaching): string =>
  hatRueckzug(coaching) ? ZURUECKGEZOGEN : coaching.stage

function coachingCardTone(schwellen: AmpelSchwellen, now: Date) {
  return (coaching: DashboardCoaching): CardTone | undefined => {
    const ampel = ampelFor(tageInSpalte(coaching.statusSeit, now), schwellen)
    return ampel === 'rot' ? 'danger' : ampel === 'gelb' ? 'warning' : undefined
  }
}

function boardColumns(
  schwellen: AmpelSchwellen,
  now: Date,
  incompleteIds: ReadonlySet<string>,
  marks: ReadonlyMap<string, CardMark>,
): BoardColumnConfig<DashboardCoaching>[] {
  const cardTone = coachingCardTone(schwellen, now)
  const description = descriptionFor(currentVariant())

  const onBoard = description.columns.filter((col) => !col.listOnly)
  const zurueckgezogen: BoardColumnConfig<DashboardCoaching>[] = description.rueckzug
    ? [{ id: ZURUECKGEZOGEN, title: withCompoundBreaks("Zurückgezogen"), cardAction: () => ZURUECKGEZOGEN_KARTE }]
    : []
  const stages = onBoard.map((col) => withMarks(description.cardActivity?.stage === col.id ? marks : null, {
    id: col.id,

    title: withCompoundBreaks(col.dbName),
    actionObject: col.actionObject,
    actionVerb: col.actionVerb,
    actionIcon: col.actionIcon,
    clickable: col.clickable,
    headerAction: col.headerAction,
    headerDroppable: col.headerDroppable,
    headerAccept: col.headerAccept,
    headerBusyLabel: col.headerBusyLabel,
    headerDisabledHint: col.headerDisabledHint,

    cardAction: () => col.personAction ?? 'select-coaching',

    cardTone: incompleteTone(
      (description.cardIncomplete ?? []).filter((rule) => rule.stage === col.id),
      incompleteIds,
      description.waitingLight && col.actionObject !== undefined ? cardTone : undefined,
    ),
  }))
  return [...stages, ...zurueckgezogen]
}

function incompleteTone(
  rules: readonly CardIncomplete[],
  incompleteIds: ReadonlySet<string>,
  otherwise: ((coaching: DashboardCoaching) => CardTone | undefined) | undefined,
): ((coaching: DashboardCoaching) => CardTone | undefined) | undefined {
  if (rules.length === 0) return otherwise
  return (coaching) => {
    const rule = rules.find((candidate) => cardIncompleteCovers(candidate, coaching))
    if (!rule) return otherwise?.(coaching)
    if (!rule.isIncomplete) return 'danger'
    return incompleteIds.has(coaching.id) ? 'danger' : undefined
  }
}

function withMarks(
  marks: ReadonlyMap<string, CardMark> | null,
  col: BoardColumnConfig<DashboardCoaching>,
): BoardColumnConfig<DashboardCoaching> {
  if (!marks) return col
  const { cardTone } = col
  return { ...col, cardTone: (coaching) => marks.get(coaching.id)?.tone ?? cardTone?.(coaching) }
}

export function resolveCardOpen(
  action: string,
  aufgabeName: string | null | undefined,
): { modal: Modal; tab: string } | null {
  if (action === ZURUECKGEZOGEN_KARTE) return { modal: 'personal', tab: 'details' }
  if (action === 'open-personal') return { modal: 'personal', tab: 'uebersicht' }
  const entry = aufgabeForName(aufgabeName)
  if (entry) return entry.landing
  const description = descriptionFor(currentVariant())

  const own = aufgabeName ? Object.values(description.aufgabe).find((e) => e.dbName === aufgabeName)?.landing : undefined
  if (own) return own
  const modal = description.actionToModal[action]
  if (!modal) return null
  return { modal, tab: phaseLandingTabFor(modal, aufgabeName) }
}

function renderCoachingCard(
  coaching: DashboardCoaching,
  col: BoardColumnConfig<DashboardCoaching>,
  mark: CardMark | undefined,
): ReactNode {
  if (col.id === 'nachweis' && coaching.ueGesamt != null) {
    return (
      <>
        <h3 className="card-header">{anzeigeName(coaching)}</h3>
        {coaching.aufgabe != null && <p className="card-body">{coaching.aufgabe}</p>}

        <p className="card-body">
          {`${coaching.coach ? `${coaching.coach} · ` : ''}${coaching.ueStattgefunden}/${coaching.ueGesamt} UE`}
        </p>

        <span className="card-footer">
          <span className="card-footer-label">offen</span>{' '}
          {Math.max(0, coaching.ueGesamt - coaching.ueStattgefunden)}
        </span>
      </>
    )
  }
  return (
    <>
      <h3 className="card-header">{anzeigeName(coaching)}</h3>
      {coaching.aufgabe != null && <p className="card-body">{coaching.aufgabe}</p>}

      {coaching.zurueckgezogen && <p className="card-body">{zurueckgezogenAm(coaching)}</p>}

      {mark?.busy ? (
        <CardSpinner label={mark.text} />
      ) : (
        mark && (
          <Tooltip><p className="card-body" role="status" title={mark.text}>
            {mark.text}
          </p></Tooltip>
        )
      )}
    </>
  )
}

export function coachingBoardConfig(
  schwellen: AmpelSchwellen,
  now: Date,
  incompleteIds: ReadonlySet<string>,
  marks: ReadonlyMap<string, CardMark>,
): BoardViewConfig<DashboardCoaching> {
  const description = descriptionFor(currentVariant())
  const markStage = description.cardActivity?.stage
  return {
    rowKey: coachingViewConfig.rowKey,
    groupBy: coachingBoardGroup,
    sortKeys: coachingViewConfig.sortKeys,
    searchFields: coachingViewConfig.searchFields,
    filterCategories: coachingViewConfig.filterCategories,
    excludedFor: coachingViewConfig.excludedFor,
    extraFilter: coachingViewConfig.extraFilter,
    columns: boardColumns(schwellen, now, incompleteIds, marks),
    renderCard: (coaching, col) =>
      renderCoachingCard(
        coaching,
        col,
        col.id === markStage ? marks.get(coaching.id) : undefined,
      ),
  }
}
